import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  IconButton, 
  Tooltip, 
  Button, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  TextField, 
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  CircularProgress,
  Divider,
  Alert,
  Snackbar,
  Grid,
  Tabs,
  Tab
} from '@mui/material';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { enUS, tr } from 'date-fns/locale';
import { styled } from '@mui/material/styles';
import { format, parse, addMinutes, differenceInMinutes } from 'date-fns';
import { useParams, useNavigate } from 'react-router-dom';
import { database } from '../../../firebase';
import { ref, get, set, push, remove } from 'firebase/database';
import firebaseDataService from '../../../services/FirebaseDataService';
import { useAuth } from '../../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import GuideButton from '../../common/guide/GuideButton';

// FullCalendar bileşenleri
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';

// Simgeler
import { 
  Today as TodayIcon, 
  ChevronLeft as ChevronLeftIcon, 
  ChevronRight as ChevronRightIcon,
  ViewDay as ViewDayIcon,
  ViewWeek as ViewWeekIcon,
  CalendarMonth as MonthIcon,
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Description as DescriptionIcon,
  Group as GroupIcon,
  AccessTime as AccessTimeIcon,
  Event as EventIcon
} from '@mui/icons-material';

// Genişletilmiş rezervasyon slotu interface'i
interface ReservationSlot {
  id: string;
  title: string;
  start: string;
  end: string;
  capacity: number;
  description?: string;
  color?: string;
  borderColor?: string;
  textColor?: string; 
  createdAt: string;
  createdBy: string;
  lastModified?: string;
  lastModifiedBy?: string;
  // FullCalendar için gerekli alanlar
  editable?: boolean;
  durationEditable?: boolean;
  startEditable?: boolean;
  resourceEditable?: boolean;
  display?: string;
  backgroundColor?: string;
  groupId?: string;
  allDay?: boolean;
  // Rezervasyon durumu
  type: 'availability' | 'reservation'; // 'availability': şubenin müsait olduğu zaman, 'reservation': müşteri rezervasyonu
  status?: 'pending' | 'approved' | 'rejected'; // Sadece 'reservation' tipinde kullanılır
  customerName?: string; // Sadece 'reservation' tipinde kullanılır
  customerPhone?: string; // Sadece 'reservation' tipinde kullanılır
  customerEmail?: string; // Sadece 'reservation' tipinde kullanılır
  notes?: string; // Sadece 'reservation' tipinde kullanılır
}

interface BranchInfo {
  name: string;
  address: string;
  phone: string;
  email: string;
}

// Takvim container stil bileşeni
const CalendarContainer = styled(Box)(({ theme }) => ({
  height: 'calc(100vh - 230px)',
  maxHeight: '800px',
  overflow: 'auto',
  width: '100%',
  '& .fc': {
    '--fc-border-color': theme.palette.divider,
    '--fc-button-text-color': theme.palette.primary.main,
    '--fc-button-bg-color': 'transparent',
    '--fc-button-border-color': theme.palette.primary.main,
    '--fc-button-hover-bg-color': theme.palette.primary.main,
    '--fc-button-hover-text-color': theme.palette.common.white,
    '--fc-button-active-bg-color': theme.palette.primary.dark,
    '--fc-today-bg-color': `${theme.palette.primary.main}10`,
    '--fc-event-bg-color': theme.palette.primary.main,
    '--fc-event-border-color': theme.palette.primary.dark,
    '--fc-page-bg-color': theme.palette.background.paper,
    '--fc-neutral-bg-color': theme.palette.background.default,
    fontFamily: theme.typography.fontFamily,
  },
  '& .fc .fc-toolbar': {
    flexWrap: 'wrap',
    gap: '10px',
    marginBottom: '1rem'
  },
  '& .fc .fc-toolbar-title': {
    fontSize: '1.5rem',
    fontWeight: 700,
    color: theme.palette.text.primary
  },
  '& .fc-theme-standard td, .fc-theme-standard th': {
    borderColor: theme.palette.divider
  },
  '& .fc .fc-day-today': {
    backgroundColor: `${theme.palette.primary.main}10`,
  },
  '& .fc-day-header': {
    fontWeight: 600,
    padding: '10px 0'
  },
  '& .fc-event': {
    borderRadius: '4px',
    padding: '2px 4px',
    fontSize: '0.85rem',
    cursor: 'pointer',
    borderWidth: '0',
    boxShadow: '0 2px 5px rgba(0,0,0,0.08)',
    transition: 'transform 0.1s ease-in-out, box-shadow 0.1s ease-in-out',
    '&:hover': {
      transform: 'translateY(-1px) scale(1.01)',
      boxShadow: '0 4px 8px rgba(0,0,0,0.12)'
    }
  },
  '& .fc-col-header-cell': {
    padding: '8px 0'
  },
  '& .fc-day-number': {
    fontWeight: 500,
    padding: '5px'
  },
  '& .fc-daygrid-event': {
    whiteSpace: 'normal'
  },
  // Mobil uyumluluk için
  [theme.breakpoints.down('sm')]: {
    '& .fc-toolbar': {
      flexDirection: 'column',
      alignItems: 'center'
    },
    '& .fc-dayGridMonth-view .fc-daygrid-day-events': {
      margin: '0 2px'
    },
    '& .fc-header-toolbar .fc-toolbar-chunk': {
      marginBottom: '8px'
    }
  }
}));

// Özel toolbar bileşeni
const CalendarToolbar = ({ 
  calendarRef, 
  view, 
  setView 
} : { 
  calendarRef: React.RefObject<FullCalendar | null>, 
  view: string, 
  setView: (view: string) => void 
}) => {
  const { t, i18n } = useTranslation('other');
  const adapterLocale = i18n.language?.startsWith('tr') ? tr : enUS;
  
  const [currentDate, setCurrentDate] = useState<Date>(new Date());
  
  useEffect(() => {
    const calendar = calendarRef.current;
    if (calendar) {
      const api = calendar.getApi();
      setCurrentDate(api.getDate());
    }
  }, [calendarRef, view]);
  
  const handlePrev = () => {
    const calendarApi = calendarRef.current?.getApi();
    if (calendarApi) {
      calendarApi.prev();
      setCurrentDate(calendarApi.getDate());
    }
  };

  const handleNext = () => {
    const calendarApi = calendarRef.current?.getApi();
    if (calendarApi) {
      calendarApi.next();
      setCurrentDate(calendarApi.getDate());
    }
  };

  const handleToday = () => {
    const calendarApi = calendarRef.current?.getApi();
    if (calendarApi) {
      calendarApi.today();
      setCurrentDate(calendarApi.getDate());
    }
  };

  const changeView = (newView: string) => {
    const calendarApi = calendarRef.current?.getApi();
    if (calendarApi) {
      calendarApi.changeView(newView);
      setView(newView);
      
      // Görünüm değiştiğinde sayfanın boyutunu ayarlayalım
      setTimeout(() => {
        if (newView === 'timeGridDay' || newView === 'timeGridWeek') {
          document.querySelector('.fc-view-harness')?.setAttribute('style', 'height: auto !important; min-height: 700px !important');
        } else {
          document.querySelector('.fc-view-harness')?.removeAttribute('style');
        }
      }, 100);
    }
  };
  
  const formatCurrentDate = () => {
    if (view === 'timeGridDay') {
      return format(currentDate, 'd MMMM yyyy, EEEE', { locale: adapterLocale });
    }
    return format(currentDate, 'MMMM yyyy', { locale: adapterLocale });
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 1
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title={t('reservations.schedule.toolbar.previous')}>
            <IconButton onClick={handlePrev} color="primary" size="small">
              <ChevronLeftIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('reservations.schedule.toolbar.today')}>
            <IconButton onClick={handleToday} color="primary" size="small">
              <TodayIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('reservations.schedule.toolbar.next')}>
            <IconButton onClick={handleNext} color="primary" size="small">
              <ChevronRightIcon />
            </IconButton>
          </Tooltip>
          
          <Typography variant="h5" sx={{ fontWeight: 600, ml: 2 }}>
            {formatCurrentDate()}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title={t('reservations.schedule.toolbar.dayView')}>
            <IconButton 
              onClick={() => changeView('timeGridDay')} 
              color={view === 'timeGridDay' ? 'secondary' : 'default'}
              size="small"
            >
              <ViewDayIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('reservations.schedule.toolbar.weekView')}>
            <IconButton 
              onClick={() => changeView('timeGridWeek')} 
              color={view === 'timeGridWeek' ? 'secondary' : 'default'}
              size="small"
            >
              <ViewWeekIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('reservations.schedule.toolbar.monthView')}>
            <IconButton 
              onClick={() => changeView('dayGridMonth')} 
              color={view === 'dayGridMonth' ? 'secondary' : 'default'}
              size="small"
            >
              <MonthIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    </Box>
  );
};

// Tab panel bileşeni
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`reservation-tabpanel-${index}`}
      aria-labelledby={`reservation-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 2 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const BranchReservationSchedule: React.FC = () => {
  const { t, i18n } = useTranslation('other');
  const currentLocale = i18n.language?.startsWith('tr') ? 'tr-TR' : 'en-US';
  const adapterLocale = i18n.language?.startsWith('tr') ? tr : enUS;
  const { branchId } = useParams<{ branchId: string }>();
  const { currentUser, userDetails } = useAuth();
  const navigate = useNavigate();
  
  // Refs
  const calendarRef = useRef<FullCalendar | null>(null);
  
  // State'ler
  const [view, setView] = useState<string>('dayGridMonth');
  const [events, setEvents] = useState<ReservationSlot[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [modalMode, setModalMode] = useState<'create' | 'edit' | 'view' | 'delete'>('create');
  const [selectedEvent, setSelectedEvent] = useState<ReservationSlot | null>(null);
  const [branchInfo, setBranchInfo] = useState<BranchInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState<number>(0);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false);
  const [draggingInProgress, setDraggingInProgress] = useState<boolean>(false);
  
  // Form alanları için state'ler
  const [title, setTitle] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [startTime, setStartTime] = useState<Date | null>(null);
  const [endTime, setEndTime] = useState<Date | null>(null);
  const [capacity, setCapacity] = useState<number>(1);
  const [selectedColor, setSelectedColor] = useState<string>('#4CAF50');
  const [errors, setErrors] = useState<{[key: string]: string}>({});

  // Tab değişimi için handler
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Şirket ID'sini al
  const getCompanyId = async (): Promise<string | null> => {
    let companyId = null;
    
    // Kullanıcı detaylarından almayı dene
    if (userDetails?.companyId) {
      companyId = userDetails.companyId;
      console.log("CompanyId userDetails'den alındı:", companyId);
    } 
    // LocalStorage'dan almayı dene
    else if (localStorage.getItem('companyId')) {
      companyId = localStorage.getItem('companyId');
      console.log("CompanyId localStorage'dan alındı:", companyId);
    } 
    // Firebase'den kullanıcı detaylarını çek ve companyId'yi al
    else if (currentUser?.uid) {
      try {
        const userData = await firebaseDataService.getUserData(currentUser.uid);
        
        if (userData) {
          if (userData.companyId) {
            companyId = userData.companyId;
            // LocalStorage'a kaydet ki bir dahaki sefere kullanabilelim
            localStorage.setItem('companyId', companyId);
            console.log("CompanyId Firebase'den alındı:", companyId);
          }
        }
      } catch (err) {
        console.error("Kullanıcı bilgileri getirilirken hata:", err);
        setError(t('reservations.schedule.errors.userInfoLoadFailed'));
      }
    }
    
    if (!companyId) {
      setError(t('reservations.schedule.errors.companyInfoNotFound'));
      setIsLoading(false);
    }
    
    return companyId;
  };

  // Şube bilgilerini ve rezervasyon slotlarını yükleme
  useEffect(() => {
    const loadBranchData = async () => {
      if (!currentUser?.uid || !branchId) {
        setError(t('reservations.schedule.errors.requiredInfoMissing'));
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);
      
      const companyId = await getCompanyId();
      
      if (!companyId) {
        return; // getCompanyId içinde hata durumu yönetildi
      }
      
      console.log("Şube ve rezervasyon bilgileri yükleniyor. CompanyId:", companyId, "BranchId:", branchId);
      
      // Şube bilgilerini yükle (SQL — firebaseDataService.getBranches SQL-once)
      try {
        const allBranches = await firebaseDataService.getBranches(companyId);
        const branch = allBranches.find(b => b.id === branchId);

        if (branch) {
          setBranchInfo({
            name: branch.name || '',
            address: branch.address || '',
            phone: branch.phone || '',
            email: '',
          });
        } else {
          console.error("Branch not found");
          setError(t('reservations.schedule.errors.branchNotFound'));
          setIsLoading(false);
          return;
        }
      } catch (err) {
        console.error("Error while loading branch information:", err);
        setError(t('reservations.schedule.errors.branchInfoLoadFailed'));
        setIsLoading(false);
        return;
      }
      
      // Rezervasyon slotlarını yükle (TEK SEFERLİK)
      const reservationsRef = ref(database, `companies/${companyId}/branches/${branchId}/reservationSlots`);
      
      try {
        const snapshot = await get(reservationsRef);
        
        if (snapshot.exists()) {
          const reservationData = snapshot.val();
          const reservationArray = Object.keys(reservationData).map(key => {
            const event = {
              id: key,
              ...reservationData[key],
              editable: true,
              durationEditable: true,
              resourceEditable: true
            };
            
            if (!event.backgroundColor && event.color) {
              event.backgroundColor = event.color;
            }
            
            return event;
          });
          
          console.log("Rezervasyon slotları yüklendi:", reservationArray);
          setEvents(reservationArray);
        } else {
          console.log("Reservation slot not found");
          setEvents([]);
        }
      } catch (err) {
        console.error("Error while loading reservation data:", err);
        setError(t('reservations.schedule.errors.reservationDataLoadFailed'));
      } finally {
        setIsLoading(false);
      }
    };
    
    loadBranchData();
  }, [currentUser, branchId, userDetails]);

  // Seçilen tarihte yeni slot oluşturma (takvimde bir gün/zaman aralığına tıklandığında)
  const handleDateClick = (info: any) => {
    setSelectedDate(new Date(info.date));
    setModalMode('create');
    resetForm();
    
    // Tıklanan zamana göre varsayılan değerler
    const clickedDate = new Date(info.date);
    setStartDate(clickedDate);
    setEndDate(clickedDate);
    
    // Saat görünümünde tıklanmışsa, saat bilgisini al
    if (info.view.type === 'timeGridDay' || info.view.type === 'timeGridWeek') {
      setStartTime(clickedDate);
      
      // Varsayılan süre: 1 saat
      const defaultEndTime = new Date(clickedDate);
      defaultEndTime.setHours(defaultEndTime.getHours() + 1);
      setEndTime(defaultEndTime);
    } else {
      // Gün görünümünde tıklanmışsa, varsayılan saat değerleri
      const today = new Date(clickedDate);
      today.setHours(9, 0, 0, 0); // 09:00
      setStartTime(today);
      
      const endTimeToday = new Date(clickedDate);
      endTimeToday.setHours(10, 0, 0, 0); // 10:00
      setEndTime(endTimeToday);
    }
    
    setOpenModal(true);
  };
  
  // Seçim (selection) işleyicisi - takvimde sürükleyerek seçim yapıldığında
  const handleDateSelect = (info: any) => {
    const { start, end } = info;
    
    setModalMode('create');
    resetForm();
    
    // Seçilen tarih aralığını ayarla
    setStartDate(new Date(start));
    setEndDate(new Date(end));
    setStartTime(new Date(start));
    setEndTime(new Date(end));
    setSelectedDate(new Date(start));
    
    setOpenModal(true);
  };

  // Var olan slotu düzenlemek için tıklama işleyicisi
  const handleEventClick = (info: any) => {
    const event = events.find(e => e.id === info.event.id);
    if (event) {
      setSelectedEvent(event);
      setModalMode('view'); // İlk önce görüntüleme modunda aç
      
      // Form alanlarını doldur
      setTitle(event.title || '');
      setDescription(event.description || '');
      
      // Tarih ve saat değerlerini ayarla
      const startDateTime = new Date(event.start);
      const endDateTime = new Date(event.end);
      
      setStartDate(startDateTime);
      setEndDate(endDateTime);
      setStartTime(startDateTime);
      setEndTime(endDateTime);
      
      // Kapasite ve renk
      setCapacity(event.capacity || 1);
      setSelectedColor(event.color || '#4CAF50');
      
      setOpenModal(true);
    }
  };
  
  // Etkinlik sürükleme işleyicisi
  const handleEventDrop = async (info: any) => {
    setDraggingInProgress(true);
    
    try {
      const { event, oldEvent } = info;
      const companyId = await getCompanyId();
      
      if (!companyId || !branchId) {
        throw new Error("Company or branch information not found");
      }
      
      // Güncellenen event bilgileri
      const updatedEvent: Partial<ReservationSlot> = {
        ...events.find(e => e.id === event.id),
        start: event.start.toISOString(),
        end: event.end.toISOString(),
        lastModified: new Date().toISOString(),
        lastModifiedBy: currentUser?.uid || ''
      };
      
      // Firebase'de güncelle
      await set(ref(database, `companies/${companyId}/branches/${branchId}/reservationSlots/${event.id}`), updatedEvent);
      
      console.log("Etkinlik taşındı:", updatedEvent);
    } catch (error) {
      console.error("Event move error:", error);
      setError(t('reservations.schedule.errors.reservationMoveFailed'));
      info.revert(); // Taşımayı geri al
    } finally {
      setDraggingInProgress(false);
    }
  };
  
  // Etkinlik boyutlandırma işleyicisi
  const handleEventResize = async (info: any) => {
    setDraggingInProgress(true);
    
    try {
      const { event } = info;
      const companyId = await getCompanyId();
      
      if (!companyId || !branchId) {
        throw new Error("Company or branch information not found");
      }
      
      // Güncellenen event bilgileri
      const updatedEvent: Partial<ReservationSlot> = {
        ...events.find(e => e.id === event.id),
        start: event.start.toISOString(),
        end: event.end.toISOString(),
        lastModified: new Date().toISOString(),
        lastModifiedBy: currentUser?.uid || ''
      };
      
      // Firebase'de güncelle
      await set(ref(database, `companies/${companyId}/branches/${branchId}/reservationSlots/${event.id}`), updatedEvent);
      
      console.log("Etkinlik boyutlandırıldı:", updatedEvent);
    } catch (error) {
      console.error("Event resize error:", error);
      setError(t('reservations.schedule.errors.reservationResizeFailed'));
      info.revert(); // Boyutlandırmayı geri al
    } finally {
      setDraggingInProgress(false);
    }
  };
  
  // Form alanlarını sıfırla
  const resetForm = () => {
    setTitle('');
    setDescription('');
    setStartDate(null);
    setEndDate(null);
    setStartTime(null);
    setEndTime(null);
    setCapacity(1);
    setSelectedColor('#4CAF50');
    setErrors({});
  };
  
  // Modal kapatma işleyicisi
  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedEvent(null);
    resetForm();
  };
  
  // Hata bildirimi kapatma
  const handleCloseError = () => {
    setError(null);
  };
  
  // Form doğrulama
  const validateForm = () => {
    const newErrors: {[key: string]: string} = {};
    
    if (!title.trim()) {
      newErrors.title = t('reservations.schedule.validation.titleRequired');
    }
    
    if (!startDate) {
      newErrors.startDate = t('reservations.schedule.validation.startDateRequired');
    }
    
    if (!endDate) {
      newErrors.endDate = t('reservations.schedule.validation.endDateRequired');
    } else if (startDate && endDate && startDate > endDate) {
      newErrors.endDate = t('reservations.schedule.validation.endDateAfterStart');
    }
    
    if (!startTime) {
      newErrors.startTime = t('reservations.schedule.validation.startTimeRequired');
    }
    
    if (!endTime) {
      newErrors.endTime = t('reservations.schedule.validation.endTimeRequired');
    } else if (
      startDate && 
      endDate && 
      startDate.getTime() === endDate.getTime() && 
      startTime && 
      endTime && 
      startTime >= endTime
    ) {
      newErrors.endTime = t('reservations.schedule.validation.endTimeAfterStart');
    }
    
    if (capacity < 1) {
      newErrors.capacity = t('reservations.schedule.errors.capacityMinOne');
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  // Düzenleme moduna geçme
  const handleEditMode = () => {
    setModalMode('edit');
  };
  
  // Silme moduna geçme
  const handleDeleteMode = () => {
    setShowDeleteConfirm(true);
  };
  
  // Rezervasyon silme
  const handleDeleteReservation = async () => {
    if (!selectedEvent || !branchId) return;
    
    try {
      const companyId = await getCompanyId();
      
      if (!companyId) {
        setError(t('reservations.schedule.errors.companyInfoNotFound'));
        return;
      }
      
      // Firebase'den rezervasyonu sil
      await remove(ref(database, `companies/${companyId}/branches/${branchId}/reservationSlots/${selectedEvent.id}`));
      
      setShowDeleteConfirm(false);
      handleCloseModal();
    } catch (error) {
      console.error("Reservation deletion error:", error);
      setError(t('reservations.schedule.errors.reservationDeleteFailed'));
    }
  };
  
  // Rezervasyon slotu kaydetme işlevi
  const handleSaveReservation = async () => {
    if (!validateForm() || !currentUser?.uid || !branchId || !startDate || !endDate) {
      return;
    }
    
    try {
      const companyId = await getCompanyId();
      
      if (!companyId) {
        return; // getCompanyId içinde hata durumu yönetildi
      }
      
      // Başlangıç ve bitiş tarihleri oluştur
      const startDateTime = new Date(startDate);
      const endDateTime = new Date(endDate);
      
      if (startTime) {
        startDateTime.setHours(startTime.getHours());
        startDateTime.setMinutes(startTime.getMinutes());
      }
      
      if (endTime) {
        endDateTime.setHours(endTime.getHours());
        endDateTime.setMinutes(endTime.getMinutes());
      }
      
      // Yeni rezervasyon slotu verileri
      const slotData: Omit<ReservationSlot, 'id'> = {
        title,
        description: description || '',
        start: startDateTime.toISOString(),
        end: endDateTime.toISOString(),
        capacity: capacity,
        color: selectedColor,
        borderColor: selectedColor,
        textColor: '#ffffff',
        createdAt: new Date().toISOString(),
        createdBy: currentUser.uid,
        editable: true,
        durationEditable: true,
        startEditable: true,
        type: 'availability' // Bu sayfa sadece müsait saatleri belirlemek için kullanılıyor
      };
      
      if (modalMode === 'edit') {
        slotData.lastModified = new Date().toISOString();
        slotData.lastModifiedBy = currentUser.uid;
      }
      
      const reservationsRef = ref(database, `companies/${companyId}/branches/${branchId}/reservationSlots`);
      
      if (modalMode === 'create') {
        // Yeni kayıt ekle
        console.log("Yeni müsaitlik slotu ekleniyor:", slotData);
        await push(reservationsRef, slotData);
      } else if (modalMode === 'edit' && selectedEvent) {
        // Var olan kaydı güncelle
        console.log("Müsaitlik slotu güncelleniyor:", slotData);
        await set(ref(database, `companies/${companyId}/branches/${branchId}/reservationSlots/${selectedEvent.id}`), slotData);
      }
      
      handleCloseModal();
      
    } catch (error) {
      console.error('Availability save error:', error);
      setError(t('reservations.schedule.errors.availabilitySaveFailed'));
    }
  };
  
  // Geri dönme yönlendirmesi
  const handleBack = () => {
    navigate('/reservations');
  };

  return (
    <Paper sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column', mb: 4 }}>
      {/* Başlık alanı */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Tooltip title={t('reservations.schedule.actions.back')}>
            <IconButton onClick={handleBack}>
              <ArrowBackIcon />
            </IconButton>
          </Tooltip>
          <Typography variant="h5" component="h1" sx={{ ml: 1 }}>
            {t('reservations.schedule.headerTitle', { branch: branchInfo?.name || t('reservations.schedule.branchFallback') })}
          </Typography>
          <GuideButton guideKey="reservations.schedule" size="small" sx={{ ml: 0.5 }} />
        </Box>
        
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button 
            variant="outlined" 
            color="primary" 
            onClick={() => navigate(`/reservations/branch/${branchId}/requests`)}
            sx={{ mr: 1 }}
          >
            {t('reservations.schedule.actions.viewReservationRequests')}
          </Button>
          
          <Button
            variant="outlined"
            color="secondary"
            onClick={() => {
              const url = `${window.location.origin}/reservation/${userDetails?.companyId}/${branchId}`;
              navigator.clipboard.writeText(url);
              alert(t('reservations.schedule.messages.reservationLinkCopied', { url }));
            }}
          >
            {t('reservations.schedule.actions.copyReservationLink')}
          </Button>
          
          <Button 
            variant="contained" 
            color="primary" 
            startIcon={<AddIcon />}
            onClick={() => {
              setSelectedDate(new Date());
              setModalMode('create');
              resetForm();
              setOpenModal(true);
            }}
          >
            {t('reservations.schedule.actions.addNewAvailability')}
          </Button>
        </Box>
      </Box>
      
      {/* Şube bilgileri */}
      {branchInfo && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2">
            {t('reservations.schedule.branchAddress', { address: branchInfo.address })}
          </Typography>
          {branchInfo.phone && (
            <Typography variant="body2">
              {t('reservations.schedule.branchPhone', { phone: branchInfo.phone })}
            </Typography>
          )}
        </Box>
      )}
      
      <Divider sx={{ my: 2 }} />
      
      {/* Hata bildirimi */}
      <Snackbar open={!!error} autoHideDuration={6000} onClose={handleCloseError}>
        <Alert onClose={handleCloseError} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
      
      {/* Takvim alanı */}
      <CalendarContainer>
        <CalendarToolbar calendarRef={calendarRef} view={view} setView={setView} />
        
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '500px' }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            
            <FullCalendar
              ref={calendarRef}
              plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
              initialView={view}
              headerToolbar={false}
              events={events}
              eventTimeFormat={{
                hour: '2-digit',
                minute: '2-digit',
                hour12: false
              }}
              dateClick={handleDateClick}
              eventClick={handleEventClick}
              eventDrop={handleEventDrop}
              eventResize={handleEventResize}
              select={handleDateSelect}
              selectable={true}
              selectMirror={true}
              editable={true}
              dayMaxEvents={true}
              weekends={true}
              allDaySlot={false}
              slotMinTime="06:00:00"
              slotMaxTime="23:00:00"
              slotDuration="00:30:00"
              businessHours={{
                daysOfWeek: [1, 2, 3, 4, 5, 6, 0], // 0=Pazar, 1=Pazartesi, ..., 6=Cumartesi
                startTime: '09:00',
                endTime: '18:00',
              }}
              locale={i18n.language?.startsWith('tr') ? 'tr' : 'en'}
              buttonText={{
                today: t('reservations.schedule.calendar.today'),
                month: t('reservations.schedule.calendar.month'),
                week: t('reservations.schedule.calendar.week'),
                day: t('reservations.schedule.calendar.day'),
                list: t('reservations.schedule.calendar.list')
              }}
            />
          </>
        )}
      </CalendarContainer>
      
      {/* Rezervasyon detay/düzenleme modal */}
      <Dialog 
        open={openModal} 
        onClose={handleCloseModal} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle>
          {modalMode === 'create' ? t('reservations.schedule.modal.titleCreate') : 
           modalMode === 'edit' ? t('reservations.schedule.modal.titleEdit') : 
           t('reservations.schedule.modal.titleView')}
        </DialogTitle>
        
        <DialogContent dividers>
          <Box sx={{ mt: 1 }}>
            <Tabs 
              value={tabValue}
              onChange={handleTabChange}
              aria-label={t('reservations.schedule.modal.tabsAriaLabel')}
            >
              <Tab 
                icon={<EventIcon fontSize="small" />} 
                iconPosition="start" 
                label={t('reservations.schedule.modal.tabs.basicInfo')} 
              />
              <Tab 
                icon={<DescriptionIcon fontSize="small" />} 
                iconPosition="start" 
                label={t('reservations.schedule.modal.tabs.description')} 
              />
            </Tabs>
            
            <TabPanel value={tabValue} index={0}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    label={t('reservations.schedule.modal.fields.availabilityTitle')}
                    fullWidth
                    variant="outlined"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    error={!!errors.title}
                    helperText={errors.title}
                    placeholder={t('reservations.schedule.modal.fields.availabilityTitlePlaceholder')}
                    disabled={modalMode === 'view'}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={adapterLocale}>
                    <DatePicker
                      label={t('reservations.schedule.modal.fields.startDate')}
                      value={startDate}
                      onChange={(newValue) => setStartDate(newValue)}
                      slotProps={{
                        textField: {
                          variant: 'outlined',
                          fullWidth: true,
                          error: !!errors.startDate,
                          helperText: errors.startDate
                        }
                      }}
                      disabled={modalMode === 'view'}
                    />
                  </LocalizationProvider>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={adapterLocale}>
                    <DatePicker
                      label={t('reservations.schedule.modal.fields.endDate')}
                      value={endDate}
                      onChange={(newValue) => setEndDate(newValue)}
                      slotProps={{
                        textField: {
                          variant: 'outlined',
                          fullWidth: true,
                          error: !!errors.endDate,
                          helperText: errors.endDate
                        }
                      }}
                      disabled={modalMode === 'view'}
                    />
                  </LocalizationProvider>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={adapterLocale}>
                    <TimePicker
                      label={t('reservations.schedule.modal.fields.startTime')}
                      value={startTime}
                      onChange={(newValue) => setStartTime(newValue)}
                      slotProps={{
                        textField: {
                          variant: 'outlined',
                          fullWidth: true,
                          error: !!errors.startTime,
                          helperText: errors.startTime
                        }
                      }}
                      disabled={modalMode === 'view'}
                    />
                  </LocalizationProvider>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={adapterLocale}>
                    <TimePicker
                      label={t('reservations.schedule.modal.fields.endTime')}
                      value={endTime}
                      onChange={(newValue) => setEndTime(newValue)}
                      slotProps={{
                        textField: {
                          variant: 'outlined',
                          fullWidth: true,
                          error: !!errors.endTime,
                          helperText: errors.endTime
                        }
                      }}
                      disabled={modalMode === 'view'}
                    />
                  </LocalizationProvider>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth error={!!errors.capacity} disabled={modalMode === 'view'}>
                    <InputLabel id="capacity-label">{t('reservations.schedule.modal.fields.capacity')}</InputLabel>
                    <Select
                      labelId="capacity-label"
                      value={capacity}
                      label={t('reservations.schedule.modal.fields.capacity')}
                      onChange={(e) => setCapacity(Number(e.target.value))}
                    >
                      {[...Array(50)].map((_, index) => (
                        <MenuItem key={index + 1} value={index + 1}>{index + 1}</MenuItem>
                      ))}
                    </Select>
                    {errors.capacity && (
                      <FormHelperText>{errors.capacity}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    label={t('reservations.schedule.modal.fields.color')}
                    fullWidth
                    type="color"
                    variant="outlined"
                    value={selectedColor}
                    onChange={(e) => setSelectedColor(e.target.value)}
                    disabled={modalMode === 'view'}
                    InputProps={{
                      startAdornment: (
                        <Box 
                          sx={{ 
                            width: 24, 
                            height: 24, 
                            borderRadius: '50%', 
                            backgroundColor: selectedColor,
                            mr: 1
                          }}
                        />
                      ),
                    }}
                  />
                </Grid>
              </Grid>
            </TabPanel>
            
            <TabPanel value={tabValue} index={1}>
              <TextField
                label={t('reservations.schedule.modal.fields.description')}
                multiline
                rows={6}
                fullWidth
                variant="outlined"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={t('reservations.schedule.modal.fields.descriptionPlaceholder')}
                disabled={modalMode === 'view'}
              />
            </TabPanel>
            
            {modalMode === 'view' && selectedEvent && (
              <Box sx={{ mt: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                <Typography variant="subtitle2" color="textSecondary">
                  {t('reservations.schedule.modal.createdAt', { date: new Date(selectedEvent.createdAt).toLocaleString(currentLocale) })}
                </Typography>
                {selectedEvent.lastModified && (
                  <Typography variant="subtitle2" color="textSecondary">
                    {t('reservations.schedule.modal.lastModified', { date: new Date(selectedEvent.lastModified).toLocaleString(currentLocale) })}
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        </DialogContent>
        
        <DialogActions>
          {modalMode === 'view' ? (
            <>
              <Button onClick={handleCloseModal}>{t('reservations.schedule.actions.close')}</Button>
              <Button 
                onClick={handleDeleteMode} 
                color="error" 
                startIcon={<DeleteIcon />}
              >
                {t('reservations.schedule.actions.delete')}
              </Button>
              <Button 
                onClick={handleEditMode} 
                color="primary" 
                variant="contained"
                startIcon={<EditIcon />}
              >
                {t('reservations.schedule.actions.edit')}
              </Button>
            </>
          ) : (
            <>
              <Button onClick={handleCloseModal}>{t('reservations.schedule.actions.cancel')}</Button>
              <Button 
                onClick={handleSaveReservation} 
                variant="contained" 
                color="primary"
                startIcon={<SaveIcon />}
              >
                {t('reservations.schedule.actions.save')}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
      
      {/* Silme onay dialogu */}
      <Dialog
        open={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
      >
        <DialogTitle>
          {t('reservations.schedule.deleteDialog.title')}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {t('reservations.schedule.deleteDialog.message')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteConfirm(false)}>{t('reservations.schedule.actions.cancel')}</Button>
          <Button 
            onClick={handleDeleteReservation} 
            color="error" 
            variant="contained"
          >
            {t('reservations.schedule.actions.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default BranchReservationSchedule; 